<script lang="ts" setup>
import { computed } from 'vue'
import type { ModelRef } from 'vue'

import {
	CPButton,
	CPSvgIcon
} from '~/portal/components/base'
import { CPTypography } from '~/portal/components/layout'

import type { ICPDialogPropTypes } from './index.ts'

const props = withDefaults(
	defineProps<ICPDialogPropTypes>(),
	{
		modelValue: false,
		scrollable: false,
		fullscreen: false,
		maxWidth: 528,
		maxHeight: '100%',
		persistent: false,
		persistentBounce: true,
		closeOnBack: true,
		disabledFooter: false,
		loading: false,
		size: 'default',
		colorConfirm: 'main',
		showCancel: true,
		hideOverflow: false
	}
)

const emit = defineEmits<{
  (e: 'click:outside'): void
  (e: 'press:esc'): void
  (e: 'update:modelValue', value: boolean): void
  (e: 'close'): void
  (e: 'click:close'): void
  (e: 'click:cancel'): void
  (e: 'click:confirm'): void
}>()

const model: ModelRef<ICPDialogPropTypes['modelValue']> = defineModel()

const onClose = (): void => {
	emit('close')
	emit('click:close')
	model.value = false
}

const onClickOutside = (): void => {
	model.value = false
	emit('click:outside')
}
const onESC = (): void => {
	model.value = false
	emit('press:esc')
}
const onCancelClick = (): void => {
	model.value = false
	emit('click:cancel')
}
const onConfirmClick = (): void => emit('click:confirm')

const computedPersistent = computed(() =>
	props.persistent || props.persistent === ''
)
</script>

<script lang="ts">
export default { inheritAttrs: false }
</script>

<template>
  <VDialog
    v-model="model"
    :class="[
      'cp-dialog',
      $attrs.class,
			{ 'cp-dialog--size-sm': size === 'sm' }
    ]"
    :height="height"
    :max-height="maxHeight"
    :max-width="maxWidth"
    :no-click-animation="persistentBounce"
    :width="width"
    :fullscreen="fullscreen"
    :persistent="computedPersistent"
    :close-on-back="closeOnBack"
    @click:outside="onClickOutside"
    @keyup.esc="onESC"
  >
    <div
      :class="[
        'cp-dialog__background',
        {
          'cp-dialog__background--full': fullscreen
        },
        {
          'cp-dialog__background--hide-overflow': hideOverflow,
        }
      ]"
    >
      <slot name="custom">
        <div
          :class="[
            'cp-dialog__wrapper',
            {
              'cp-dialog__wrapper--no-footer': disabledFooter,
            },
           {
              'cp-dialog__wrapper--hide-overflow': hideOverflow,
            }
          ]"
        >
          <CPSvgIcon
            name="close"
            class="cp-dialog__button-close"
            @click="onClose"
          />

        <div
          :class="[
            'cp-dialog__main',
            { 'cp-dialog__main--scrollable': scrollable }
          ]"
        >
          <div class="cp-dialog__main-inner">
            <slot name="header">
              <CPTypography
                v-if="title"
                :label="title"
                variant="h2"
                class="cp-dialog__title"
              />
              <CPTypography
                v-if="description"
                :label="description"
                variant="body1"
                class="cp-dialog__description"
              />
            </slot>

                <div v-if="$slots.default" class="cp-dialog__content">
                  <slot :close="onClose" />
                </div>
              </div>
            </div>

            <div v-if="!disabledFooter" class="cp-dialog__footer">
              <slot name="footer">
                <div
                  :class="[
                    'cp-dialog__footer-actions',
                    {'cp-dialog__footer-actions--solo': !showCancel},
                    actions?.classes
                  ]"
                >
                  <slot name="actions">
                    <slot name="actions-before" />

                    <CPButton
                      @click="onConfirmClick"
                      :color="colorConfirm"
                      :loading="loading"
                      :disabled="disabledConfirm"
                    >
                      {{ actions?.confirm || 'Speichern' }}
                    </CPButton>

                    <CPButton
                      v-if="showCancel"
                      variant="outlined"
                      color="neutral"
                      @click="onCancelClick"
                    >
                      {{ actions?.cancel || 'Abbrechen' }}
                    </CPButton>

                    <slot name="actions-after" />
                  </slot>
                </div>
              </slot>
            </div>
          </div>
      </slot>
    </div>
  </VDialog>
</template>

<style lang="scss" scoped>
.cp-dialog {
  @apply
    max-w-full
  ;

  &:not(.v-dialog--fullscreen) {
    .v-overlay__content{
      max-width: calc((100% - 24px));
    }
  }

  &__main {
    $main: &;

    &-inner {
      @apply
        h-full
      ;
    }

    &--scrollable {
      @apply
        overflow-auto
      ;
    }
  }

  &__title {
    @apply
    color-cp-neutral-700
      text-center
      mb-3
    ;
  }

  &__description {
    @apply
      mt-3
      text-center
    color-cp-neutral-500
    ;
  }

  &__background {
    @apply
      flex
      flex-grow
      bg-white
      rounded
      overflow-y-auto
    ;

    box-shadow: 0 4px 12px 0 #00000014;

    &--full {
      @apply
        b-rd-0
        h-full
        overflow-y-auto
      ;
    }

    &--hide-overflow {
      @apply
        overflow-y-initial
      ;
    }
  }

  &__wrapper {
    @apply
      px-4
      pt-8
      sm:(
        px-16
        pt-12
      )
      w-full
      max-h-full
      overflow-y-auto
    ;

    &--hide-overflow {
      @apply
        overflow-y-initial
      ;
    }

    &--no-footer {
      @apply
        pb-8
        sm:pb-12
      ;
    }
  }

  &__content {
    @apply
      mt-10
      mb-6
    ;
  }

  &__footer {
    // sticky
    // bottom-0
    @apply
      pt-4
      bg-white
      pb-8
      sm:pb-12
    ;

    &-actions {
      @apply
        grid
        gap-4
        sm:grid-cols-2
      ;

      &--solo {
        @apply
          sm:grid-cols-1
        ;
      }
    }
  }

  &__button-close {
    @apply
      absolute
      top-0
      right-0
      sm:(
        top-4
        right-4
      )
      p-2
      cursor-pointer
      text-cp-neutral-500
      bg-white
      z-1
    ;
  }

  &--size {
    &-sm {
      .cp-dialog {
        &__wrapper  {
          @apply
            px-4
            py-10
          ;
        }

        &__content {
          @apply
            mt-8
            mb-4
          ;
        }

        &__button-close {
          @apply
            top-2
            right-2
            p-1
          ;
        }
      }
    }
  }

  .v-overlay__scrim {
    @apply
      opacity-30
    ;
  }
}
</style>
